import { currencySymbol } from '@todo/core/i18n/format';
import { intlLocaleTag, type Locale } from '@todo/core/i18n/locales';

/** "€120" for EUR (a symbol written before the amount), else "120 AED". */
export function formatPrice(amount: number, currency: string | null | undefined, locale: Locale): string {
  const n = new Intl.NumberFormat(intlLocaleTag(locale), { maximumFractionDigits: 0 }).format(amount);
  const symbol = currencySymbol(currency);
  if (!symbol) return n;
  return symbol.length === 1 ? `${symbol}${n}` : `${n} ${symbol}`;
}

/**
 * AED → EUR for the "≈ €" after an event's price. The dirham is pegged to the
 * US dollar, so this only moves with EUR/USD: review it once a quarter
 * (2026-10-28: 1 AED ≈ €0.235).
 */
export const AED_TO_EUR = 0.235;

/**
 * An event's ticket price: "AED 150 (≈ €35)" — Platinumlist sells in dirhams,
 * our visitors think in euros. Any other currency is formatPrice's.
 */
export function formatEventPrice(amount: number, currency: string | null | undefined, locale: Locale): string {
  if ((currency ?? '').toUpperCase() !== 'AED') return formatPrice(amount, currency, locale);
  const nf = new Intl.NumberFormat(intlLocaleTag(locale), { maximumFractionDigits: 0 });
  return `AED ${nf.format(amount)} (≈ €${nf.format(Math.max(1, Math.round(amount * AED_TO_EUR)))})`;
}

/** The rating to one decimal, in the locale's separator ("4,8" in DE/RU). */
export function formatRating(rating: number, locale: Locale): string {
  return new Intl.NumberFormat(intlLocaleTag(locale), {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(rating);
}

// Units of a tour duration per UI locale. The stored string is English
// (sync-viator's humanizeMinutes(): "45 min", "3 h 30 min", "5 h – 6 h",
// "from 3 h"), so it is relabelled for display only — durationMinutes() and
// the SQL filters keep reading the stored form. Keyed by every language a site
// may enable, not just this site's; English needs no entry.
const DURATION_UNITS: Partial<Record<string, { h: string; min: string; from: string }>> = {
  de: { h: 'Std.', min: 'Min.', from: 'ab' },
  es: { h: 'h', min: 'min', from: 'desde' },
  ru: { h: 'ч', min: 'мин', from: 'от' },
};

/** A tour's duration in the locale's units: "5 h – 6 h" → "5 ч – 6 ч" in RU, "5 Std. – 6 Std." in DE. */
export function formatDuration(duration: string, locale: Locale): string {
  const units = DURATION_UNITS[locale];
  if (!units) return duration;
  return duration
    .replace(/^from\b/i, units.from)
    .replace(/(\d)\s*h\b/g, `$1 ${units.h}`)
    .replace(/(\d)\s*min\b/g, `$1 ${units.min}`);
}

/** Path under a locale: localePath('de', 'tours') → "/de/tours". */
export function localePath(locale: Locale, path = ''): string {
  const clean = path.replace(/^\/+|\/+$/g, '');
  return `/${locale}${clean ? `/${clean}` : ''}`;
}

// Display names of the affiliate programs (tours.source / boats.source / events.source), for
// the "via …" / "You’ll book on …" line under a booking button.
const PARTNER_NAMES: Record<string, string> = {
  viator: 'Viator',
  click_and_boat: 'Click&Boat',
  platinumlist: 'Platinumlist',
};

/** "Click&Boat" for 'click_and_boat'; null for a source without a display name. */
export function partnerName(source: string | null | undefined): string | null {
  return (source && PARTNER_NAMES[source]) || null;
}
